/** @jsxImportSource @emotion/react */
import React, { useEffect } from "react";
import * as yup from "yup";
import * as authSelectors from "../../auth/authSelectors";
import { FullScreenModal } from "../../../components/FullScreenModal";
import { colors } from "../../../components/colors";
import {
  MenuOverwrite,
  MenuProduct,
  MutateMenuOverwritePayload,
  MutateMenuOverwriteProduct,
  OverwriteType,
} from "../models/Menus";
import { css } from "@emotion/react";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import {
  useCreateMenuOverwrite,
  useDeleteMenuOverwrite,
  useGetMenuAvailableProducts,
  useGetMenuProducts,
  useUpdateMenuOverwrite,
} from "../service/menus-api";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MenuCategoryGrid } from "./MenuCategoryGrid";
import { SpinnerBlock } from "../../common/Spinner";
import { AddMenuProductsSelector } from "./AddMenuProductsSelector";
import { Divider } from "../../../components/Divider";
import { StyledText } from "../../../assets/typography/typography";
import { EmptyState } from "../../../components/EmptyState";
import { AddScheduleSelector } from "./AddScheduleSelector";
import { yupResolver } from "@hookform/resolvers/yup";
import moment, { Moment } from "moment";
import { ErrorMessage } from "@hookform/error-message";
import { BlankTextField } from "../../../components/forms/fields/BlankTextField";
import { MenuOverwriteScheduleLabel } from "./MenuOverwriteScheduleLabel";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { transformPriceInput } from "../../../components/forms/inputs/PriceInput";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { transformPriceFromAPI } from "../../../util";
import { EllipsisDropdown } from "../../../components/EllipsisDropdown";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  overwrite?: MenuOverwrite;
};

const additionalHeaderComponentStyles = css`
  width: 100%;
`;

const buttonWrapper = css`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: end;
`;

const bottomWrapper = css`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: row;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: scroll;
`;

const headerStyle = css`
  display: flex;
  flex-direction: row;
`;

const sectionBaseStyle = css`
  width: 100%;
  gap: 8px;
  padding: 24px 24px 24px 24px;
`;

const schedulesSectionWrapperStyle = css`
  display: block;
  ${sectionBaseStyle}
`;

const bottomSectionWrapperStyle = css`
  display: flex;
  flex-direction: column;
  ${sectionBaseStyle}
`;

export type OverwriteMenuModalProduct = MenuProduct & {
  priceDelivery: {
    amount: number;
    currency: string;
  };
  originalSortKey: number | string;
  overwriteValues: Omit<MutateMenuOverwriteProduct, "id" | "sortKey">;
};

export type OverwriteMenuModalFormValues = Omit<
  MutateMenuOverwritePayload,
  "products"
> & {
  products: OverwriteMenuModalProduct[];
};

const subHeaderWrapperStyle = css`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 24px 24px 24px 24px;
`;

const subHeaderTextStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

const errorWrapperStyle = css`
  display: flex;
  flex-direction: column;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 24px;
`;

const SubHeader = ({
  header,
  subtext,
  children,
}: {
  header: string;
  subtext: string;
  children: React.ReactNode;
}) => {
  return (
    <div css={subHeaderWrapperStyle}>
      <div css={subHeaderTextStyle}>
        <StyledText
          color={colors.textPrimary}
          className="bodyHeaderOneSemi mb-0"
        >
          {header}
        </StyledText>
        {subtext && (
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextRegular mb-0"
          >
            {subtext}
          </StyledText>
        )}
      </div>
      {children}
    </div>
  );
};

const schema = yup.object().shape({
  name: yup.string().required("Menu name is required"),
  description: yup.string(),
  schedules: yup
    .array()
    .of(
      yup.object().shape({
        dayOfWeek: yup.number().required(),
        startTime: yup.string().required(),
        endTime: yup.string().required(),
      })
    )
    .min(1, "You must have at least one schedule")
    .test(
      "unique-schedules",
      "You cannot have overlapping schedules",
      (value) => {
        const sortedSchedules: Record<
          string,
          {
            startTime: Moment;
            endTime: Moment;
          }[]
        > = {};
        value?.forEach((schedule) => {
          if (
            (schedule.dayOfWeek || schedule.dayOfWeek === 0) &&
            schedule.startTime &&
            schedule.endTime
          ) {
            sortedSchedules[schedule.dayOfWeek.toString()] = [
              ...(sortedSchedules[schedule.dayOfWeek.toString()] || []),
              {
                startTime: moment(schedule.startTime, "HH:mm"),
                endTime: moment(schedule.endTime, "HH:mm"),
              },
            ];
          }
        });
        let isValid = true;
        for (const [, value] of Object.entries(sortedSchedules)) {
          if (value.length === 1) {
            continue;
          }
          for (const val of value) {
            // If times are not both before startTime or both after endTime, there is an overlap
            const overlappingSchedule = value.find((v) => {
              return (
                (!val.startTime.isBefore(v.startTime) &&
                  !val.endTime.isBefore(v.startTime)) ||
                (!val.startTime.isAfter(v.endTime) &&
                  !val.endTime.isAfter(v.endTime))
              );
            });

            if (overlappingSchedule) {
              isValid = false;
              break;
            }
          }
        }

        return isValid;
      }
    ),
});

export const OverwriteMenuModal = ({ isOpen, onClose, overwrite }: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const formMethods = useForm<OverwriteMenuModalFormValues>({
    defaultValues: {
      description: overwrite?.description || "",
      name: overwrite?.name || "",
      products: [],
      schedules: [],
    },
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });

  const { reset, getFieldState, register } = formMethods;

  const {
    data: products,
    isLoading: productsAreLoading,
    error: getMenuProductsError,
  } = useGetMenuProducts(organizationKeyName, id, isOpen);

  const {
    data: availableProducts,
    isLoading: availableProductsAreLoading,
    error: getMenuAvailableProductsError,
  } = useGetMenuAvailableProducts(organizationKeyName, isOpen, id);

  const { fields, append, remove, update } = useFieldArray({
    control: formMethods.control,
    name: "products",
  });

  const {
    fields: schedulesFields,
    append: schedulesAppend,
    remove: schedulesRemove,
  } = useFieldArray({
    control: formMethods.control,
    name: "schedules",
  });

  const {
    mutateAsync: updateMenuOverwrite,
    isLoading: isUpdating,
  } = useUpdateMenuOverwrite();
  const {
    mutateAsync: createMenuOverwrite,
    isLoading: isCreating,
  } = useCreateMenuOverwrite();

  const {
    mutateAsync: deleteMenuOverwrite,
    isLoading: menuOverwriteDeleting,
  } = useDeleteMenuOverwrite();

  const { ref: nameRef, ...nameRest } = register("name");

  const onSubmit = async (data: OverwriteMenuModalFormValues) => {
    try {
      const payload: MutateMenuOverwritePayload = {
        name: data.name,
        description: data.description,
        products: data.products
          .filter((product) => {
            return (
              product.overwriteValues.type !== OverwriteType.NONE ||
              product.overwriteValues.title !== "" ||
              product.overwriteValues.priceValue !== "" ||
              product.overwriteValues.priceValueDelivery !== "" ||
              product.overwriteValues.isHidden ||
              product.overwriteValues.isUnavailable ||
              product.overwriteValues.priceDescription !== null ||
              product.sortKey !== product.originalSortKey
            );
          })
          .map((product) => {
            return {
              id: product.id,
              sortKey:
                product.sortKey !== product.originalSortKey
                  ? product.sortKey
                  : product.originalSortKey,
              type: product.overwriteValues.type || OverwriteType.CHANGE,
              ...(product.overwriteValues.title && {
                title: product.overwriteValues.title,
              }),
              ...((product.overwriteValues.priceValue ||
                product.overwriteValues.priceValue === 0) && {
                priceValue: transformPriceInput(
                  product.overwriteValues.priceValue,
                  product.price.currency
                ),
              }),
              ...((product.overwriteValues.priceValueDelivery ||
                product.overwriteValues.priceValueDelivery === 0) && {
                priceValueDelivery: transformPriceInput(
                  product.overwriteValues.priceValueDelivery,
                  product.priceDelivery.currency
                ),
              }),
              ...(product.overwriteValues.priceDescription !== undefined && {
                priceDescription: product.overwriteValues.priceDescription,
              }),
              isHidden: product.overwriteValues.isHidden,
              isUnavailable: product.overwriteValues.isUnavailable,
            };
          }),
        schedules: data.schedules,
      };

      if (overwrite) {
        await updateMenuOverwrite({
          data: { id: overwrite?.id, ...payload },
          menuId: id,
          organizationKeyName,
        });
      } else {
        await createMenuOverwrite({
          organizationKeyName,
          data: payload,
          menuId: id,
        });
      }
      toastr.success(
        "Success",
        `Menu schedule ${overwrite ? "updated" : "created"} successfully`
      );
      onClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(getMenuProductsError);
  useHandleFetchError(getMenuAvailableProductsError);

  useEffect(() => {
    if (
      !products ||
      productsAreLoading ||
      availableProductsAreLoading ||
      !availableProducts
    ) {
      return;
    }
    const formProducts: OverwriteMenuModalProduct[] = products.map(
      (product) => {
        const overwriteProduct = overwrite?.products.find(
          (p) => p.product.id === product.id
        );

        return {
          ...product,
          sortKey: overwriteProduct?.sortKey || product.sortKey,
          // Used to show what the original sort key was, so we can use it to compare if there has been a reorder.
          originalSortKey: product.sortKey,
          overwriteValues: {
            type: overwriteProduct?.type || OverwriteType.NONE,
            title: overwriteProduct?.title || "",
            priceDescription:
              overwriteProduct?.priceDescription !== undefined
                ? overwriteProduct?.priceDescription
                : undefined,
            priceValue:
              overwriteProduct?.price?.amount ||
              overwriteProduct?.price?.amount === 0
                ? transformPriceFromAPI(
                    overwriteProduct.price.amount,
                    product.price.currency
                  )
                : "",
            priceValueDelivery:
              overwriteProduct?.priceDelivery?.amount ||
              overwriteProduct?.priceDelivery?.amount === 0
                ? transformPriceFromAPI(
                    overwriteProduct.priceDelivery.amount,
                    product.priceDelivery.currency
                  )
                : "",
            isHidden: overwriteProduct ? overwriteProduct.isHidden : false,
            isUnavailable: overwriteProduct
              ? overwriteProduct.isUnavailable
              : false,
          },
        };
      }
    );

    const addedProducts = availableProducts
      .filter((p) => {
        return !!overwrite?.products.find(
          (op) => op.product.id === p.id && op.type === OverwriteType.ADD
        );
      })
      .map((product) => {
        const overwriteProduct = overwrite?.products.find(
          (p) => p.product.id === product.id
        );
        return {
          ...product,
          sortKey: overwriteProduct?.sortKey || product.sortKey,
          originalSortKey: product.sortKey,
          overwriteValues: {
            type: overwriteProduct?.type || OverwriteType.NONE,
            title: overwriteProduct?.title || "",
            priceDescription:
              overwriteProduct?.priceDescription !== undefined
                ? overwriteProduct?.priceDescription
                : undefined,
            priceValue:
              overwriteProduct?.price?.amount ||
              overwriteProduct?.price?.amount === 0
                ? transformPriceFromAPI(
                    overwriteProduct.price.amount,
                    product.price.currency
                  )
                : "",
            priceValueDelivery:
              overwriteProduct?.priceDelivery?.amount ||
              overwriteProduct?.priceDelivery?.amount === 0
                ? transformPriceFromAPI(
                    overwriteProduct.priceDelivery.amount,
                    product.priceDelivery.currency
                  )
                : "",
            isHidden: overwriteProduct ? overwriteProduct.isHidden : false,
            isUnavailable: overwriteProduct
              ? overwriteProduct.isUnavailable
              : false,
          },
        };
      });

    reset({
      products: [...formProducts, ...addedProducts],
      schedules: overwrite
        ? overwrite?.schedules.map((s) => ({
            dayOfWeek: s.dayOfWeek,
            startTime: s.fromTime,
            endTime: s.toTime,
          }))
        : [],
      name: overwrite?.name || "",
      description: overwrite?.description || "",
    });
  }, [
    products,
    reset,
    overwrite,
    productsAreLoading,
    availableProductsAreLoading,
    availableProducts,
  ]);

  return (
    <FullScreenModal
      headerComponent={
        <div css={headerStyle}>
          <BlankTextField
            placeholder="Enter name"
            innerRef={nameRef}
            additionalStyles={additionalHeaderComponentStyles}
            errorMessage={getFieldState("name").error?.message}
            {...nameRest}
          />
          {overwrite && (
            <EllipsisDropdown
              menu={{
                items: [
                  {
                    key: "1",
                    label: (
                      <DropdownItemButton
                        onClick={async () => {
                          try {
                            await deleteMenuOverwrite({
                              data: { id: overwrite.id },
                              organizationKeyName,
                            });
                            toastr.success("Success", "Menu schedule deleted");
                          } catch (error: any) {
                            const message = getErrorMessage(error);
                            toastr.error("Error", message);
                          }
                        }}
                        type="button"
                        icon={
                          menuOverwriteDeleting ? (
                            <ActionSpinner />
                          ) : (
                            <FontAwesomeIcon icon={solid("trash")} />
                          )
                        }
                        disabled={menuOverwriteDeleting}
                      >
                        Delete menu schedule
                      </DropdownItemButton>
                    ),
                  },
                ],
              }}
            />
          )}
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      {productsAreLoading ? (
        <SpinnerBlock />
      ) : (
        <div css={wrapperStyle}>
          <Divider />
          <SubHeader
            header="Availability schedule"
            subtext="Decide when this menu schedule should apply."
          >
            <AddScheduleSelector append={schedulesAppend} />
          </SubHeader>
          <Divider />
          <div css={schedulesSectionWrapperStyle}>
            {schedulesFields.length > 0 ? (
              schedulesFields.map((schedule, index) => (
                <MenuOverwriteScheduleLabel
                  key={schedule.id}
                  schedule={schedule}
                  schedulesRemove={schedulesRemove}
                  index={index}
                />
              ))
            ) : (
              <EmptyState
                title="No schedules found"
                subtitle="You can add schedules to your menu by clicking the button below."
              />
            )}
          </div>
          <ErrorMessage
            errors={formMethods.formState.errors}
            name="schedules"
            render={({ message }) => (
              <div css={errorWrapperStyle}>
                <StyledText
                  color={colors.errorDark}
                  className="bodyTextRegular mb-0"
                >
                  {message}
                </StyledText>
              </div>
            )}
          />
          <Divider />
          <FormProvider {...formMethods}>
            <SubHeader
              header="Menu"
              subtext="Add, remove and edit products for this menu schedule."
            >
              <AddMenuProductsSelector
                append={append}
                isOverwrite={true}
                availableProducts={availableProducts}
                availableProductsAreLoading={availableProductsAreLoading}
              />
            </SubHeader>
            <Divider />
            <div css={bottomSectionWrapperStyle}>
              <MenuCategoryGrid
                remove={remove}
                fields={fields}
                isOverwrite
                update={update}
                append={append}
                availableProducts={availableProducts}
                availableProductsAreLoading={availableProductsAreLoading}
                originalMenuProducts={products}
              />
            </div>
          </FormProvider>
        </div>
      )}
      <div css={bottomWrapper}>
        <div css={buttonWrapper}>
          <ButtonPrimary onClick={onClose} type="button">
            Cancel
          </ButtonPrimary>
          <ButtonAction
            type="button"
            onClick={formMethods.handleSubmit(onSubmit)}
            disabled={isUpdating || isCreating}
          >
            {isUpdating || isCreating ? <ActionSpinner /> : "Save"}
          </ButtonAction>
        </div>
      </div>
    </FullScreenModal>
  );
};
